import { type InjectedFormikProps, withFormik } from "formik";
import update from "immutability-helper";
import { useCallback, useContext, useMemo } from "react";

import { cn } from "@btnet/utilities-core";

import { type BuilderInfo } from "helpers/AppProvider.types";
import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";

import { type BTSelectItem } from "types/apiResponse/apiResponse";
import { ProposalGroupTypes } from "types/enum";

import yup from "utilities/form/yup";
import { getProposalGroupingEntityName } from "utilities/proposals/proposal.utils";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTForm, BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTLayoutContent } from "commonComponents/btWrappers/BTLayout/BTLayout";
import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTModalLayout } from "commonComponents/btWrappers/BTModal/BTModalLayout";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTSelect } from "commonComponents/btWrappers/BTSelect/BTSelect";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";

import { type ProposalFormActions } from "entity/estimate/Proposal/Proposal.types";

import "./MoveLineItems.less";

interface IMoveLineItemsProps {
    onMoveLineItems: (categoryIndex: number) => void;
    categories: BTSelectItem[];
    actionBeingPerformed: ProposalFormActions;
    viewType: ProposalGroupTypes;
    isConnectedToTakeoffs: boolean;
    isLeadProposal: boolean;
    modalConfig?: IModalConfiguration;
}

export const BTSelectItem_ParentGroupClass = "BTSelectItem_ParentGroupClass";
export const BTSelectItem_SubgroupClass = "BTSelectItem_SubgroupClass";
const BTSelectItem_MoveLineItems_SelectedClass = "BTSelectItem_MoveLineItems_SelectedClass";

interface IMoveLineItemsFormValues {
    categoryId: number | undefined;
}

const MoveLineItemsInternal: React.FC<
    InjectedFormikProps<IMoveLineItemsWithBuilderInfoProps, IMoveLineItemsFormValues>
> = ({
    values,
    categories,
    viewType,
    isConnectedToTakeoffs,
    isLeadProposal,
    actionBeingPerformed,
    modalConfig,
    onMoveLineItems,
    setFieldValue,
    handleBlur,
}) => {
    const instructionText = (
        <>
            Choose{" "}
            {getProposalGroupingEntityName(viewType, isConnectedToTakeoffs, isLeadProposal, {
                addIndefiniteArticle: true,
            })}
            {viewType === ProposalGroupTypes.Assembly &&
                ` or ${getProposalGroupingEntityName(
                    viewType,
                    isConnectedToTakeoffs,
                    isLeadProposal,
                    {
                        isSubEntity: true,
                    },
                )}`}{" "}
            to nest selected items under.
        </>
    );

    const memoTreeData = useMemo(
        () =>
            categories.map((item) =>
                Number(item.id) === values.categoryId
                    ? update(item, {
                          className: {
                              $set: cn(item.className, BTSelectItem_MoveLineItems_SelectedClass),
                          },
                      })
                    : item,
            ),
        [categories, values.categoryId],
    );

    const handleMoveLineItems = useCallback(() => {
        if (values.categoryId !== undefined) {
            onMoveLineItems(values.categoryId);
        }
    }, [values.categoryId, onMoveLineItems]);

    const actions = (
        <>
            <BTButton<ProposalFormActions>
                data-testid="apply"
                type="primary"
                onClick={handleMoveLineItems}
                disabled={values.categoryId === undefined}
                actionBeingPerformed={actionBeingPerformed}
                loadingAction="moveItems"
            >
                Save
            </BTButton>
            <BTButton
                data-testid="cancel"
                type="secondary"
                onClick={modalConfig?.beforeClose}
                actionBeingPerformed={actionBeingPerformed}
            >
                Cancel
            </BTButton>
        </>
    );

    return (
        <BTModalLayout title="Move Items" footerContent={actions} modalConfig={modalConfig}>
            <BTForm>
                <BTLayoutContent>
                    <PageSection title="Move Items">
                        <BTRow>
                            <BTCol xs={24} md={23}>
                                {instructionText}
                            </BTCol>
                        </BTRow>
                        <BTRow>
                            <BTCol xs={20}>
                                <BTFormItemAutomatic id="categoryId">
                                    <BTSelect
                                        id="categoryId"
                                        dropdownClassName="MoveLineItemsSelect"
                                        data-testid="moveItemDropdown"
                                        onChange={setFieldValue}
                                        onBlur={handleBlur}
                                        value={values.categoryId}
                                        treeData={memoTreeData}
                                    />
                                </BTFormItemAutomatic>
                            </BTCol>
                        </BTRow>
                    </PageSection>
                </BTLayoutContent>
            </BTForm>
        </BTModalLayout>
    );
};

const MoveLineItemsValidators = (props: IMoveLineItemsWithBuilderInfoProps) => {
    const { viewType, isConnectedToTakeoffs, isLeadProposal } = props;

    const baseEntityName = getProposalGroupingEntityName(
        viewType,
        isConnectedToTakeoffs,
        isLeadProposal,
        {
            isPlural: true,
        },
    );
    const label =
        viewType === ProposalGroupTypes.Assembly
            ? `${baseEntityName} and ${getProposalGroupingEntityName(
                  viewType,
                  isConnectedToTakeoffs,
                  isLeadProposal,
                  { isPlural: true, isSubEntity: true },
              )}`
            : baseEntityName;

    return yup.object().shape<IMoveLineItemsFormValues>({
        categoryId: yup.number().label(label),
    });
};

const MoveLineItemsWithFormik = withFormik<
    IMoveLineItemsWithBuilderInfoProps,
    IMoveLineItemsFormValues
>({
    mapPropsToValues: (props) => ({
        categoryId: Number(props.categories[0].id),
    }),

    // use the yup validation object we specified above
    validationSchema: MoveLineItemsValidators,
    validateOnChange: true,
    validateOnBlur: true,

    handleSubmit: async () => {},
})(MoveLineItemsInternal);

interface IMoveLineItemsWithBuilderInfoProps extends IMoveLineItemsProps {
    builderInfo?: BuilderInfo | null;
}

export const MoveLineItems: React.FC<IMoveLineItemsProps> = (props) => {
    const builderInfo = useContext(BuilderInfoContext);
    return <MoveLineItemsWithFormik {...props} builderInfo={builderInfo} />;
};
